import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Select, MenuItem, TextField } from '@mui/material';
import React, { useState } from 'react';
import TimelineDependencyChips from '../../../../components/TimelineDependencyChips/TimelineDependencyChips';
import { TimelineEventStatus } from '../../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import { TimelineEventDependency } from '../../../../slices/marketplace/timeline/models/timeline-event-dependency.model';
import { TimelineEvent } from '../../../../slices/marketplace/timeline/models/timeline-event.model';
import { MAX_DEPENDENCIES_AMOUNT } from '../../Constants/constants';
import './EventDependencyEditorCell.scss';

interface EventDependencyEditorCellProps {
    editedEvent: TimelineEvent;
    allEvents: TimelineEvent[];
    availableDependencies: TimelineEvent[];
    isDependencySelectorVisible: boolean;
    selectedDependencyId: string;
    setSelectedDependencyId(value: string): void;
    setSelectedDependencyIdForRemoval(value: string): void;
    selectedDependencyOffsetDays: number;
    setSelectedDependencyOffsetDays(value: number): void;
    handleAddDependencyClick(): void;
    confirmDependency(): void;
    setIsConfirmationDependencyRemovalVisible(value: boolean): void;
    onOffsetDaysChange?: (dependencyEventId: string, newOffset: number) => void;
}

const EventDependencyEditorCell = (props: EventDependencyEditorCellProps) => {
    const {
        editedEvent,
        allEvents,
        availableDependencies,
        isDependencySelectorVisible,
        selectedDependencyId,
        selectedDependencyOffsetDays,
        onOffsetDaysChange,
    } = props;

    return (
        <div className="event-dependency-editor-cell">
            <Box
                display="flex"
                height="100%"
                flexDirection="column"
                alignItems="start"
                justifyContent="center"
                width={'100%'}
                gap={'1rem'}>
                {editedEvent?.dependencies?.length > 0 && (
                    <Box
                        sx={{
                            width: '100%',
                            gap: '1rem',
                            display: 'flex',
                            flexDirection: 'column',
                        }}>
                        {editedEvent.dependencies.map(
                            (dep: TimelineEventDependency, index: number) => {
                                const parrentEvent = allEvents.find(
                                    (row: TimelineEvent) =>
                                        row.id === dep.dependencyEventId
                                );
                                if(parrentEvent.isSuspendEvent)
                                {
                                    return <></>;
                                }
                                const eventIndex = allEvents.findIndex(
                                    (row: TimelineEvent) =>
                                        row.id === dep.dependencyEventId
                                );

                                const isRemovable =
                                    editedEvent.status ===
                                        TimelineEventStatus.Upcoming &&
                                    parrentEvent?.status ===
                                        TimelineEventStatus.Upcoming;

                                return (
                                    <TimelineDependencyChips
                                        index={index}
                                        eventIndex={eventIndex}
                                        eventName={parrentEvent?.name}
                                        offsetDays={dep.currentTempoOffsetDays}
                                        isRemovable={isRemovable}
                                        isEditMode={true}
                                        onRemoveButtonClick={() => {
                                            props.setSelectedDependencyIdForRemoval(
                                                dep.dependencyEventId
                                            );
                                            props.setIsConfirmationDependencyRemovalVisible(
                                                true
                                            );
                                        }}
                                        onOffsetDaysChange={(newOffset) =>
                                            onOffsetDaysChange &&
                                            onOffsetDaysChange(
                                                dep.dependencyEventId,
                                                newOffset
                                            )
                                        }
                                    />
                                );
                            }
                        )}
                    </Box>
                )}

                {!isDependencySelectorVisible &&
                    editedEvent?.dependencies?.length <
                        MAX_DEPENDENCIES_AMOUNT &&
                    availableDependencies.length > 0 &&
                    editedEvent.status === TimelineEventStatus.Upcoming && (
                        <Box
                            sx={{
                                display: 'inline-block',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                color: 'primary.main',
                                lineHeight: '1.5',
                            }}
                            onClick={props.handleAddDependencyClick}>
                            <u>Add dependency</u>
                        </Box>
                    )}
                {isDependencySelectorVisible && (
                    <div className="dependency-selector-container">
                        <Select
                            className="add-dependency-selector"
                            value={selectedDependencyId}
                            onChange={(e) =>
                                props.setSelectedDependencyId(e.target.value)
                            }
                            size="small"
                            displayEmpty
                            sx={{
                                lineHeight: '1.5',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none',
                                },
                                '.MuiOutlinedInput-input': {
                                    paddingLeft: 0,
                                },
                                paddingLeft: 0,
                            }}>
                            <MenuItem value="">Select dependency</MenuItem>
                            {availableDependencies.map((row: TimelineEvent) => {
                                const eventIndex = allEvents.findIndex(
                                    (dep: TimelineEvent) => dep.id === row.id
                                );

                                return (
                                    <MenuItem key={row.id} value={row.id}>
                                        <span
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                width: '100%',
                                                maxWidth: '400px',
                                            }}>
                                            <span
                                                style={{
                                                    display: 'inline-block',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                                title={row.name}>
                                                <span className="text-bold ml-1 mr-1">
                                                    {eventIndex + 1}.
                                                </span>
                                                {row.name}
                                            </span>
                                        </span>
                                    </MenuItem>
                                );
                            })}
                        </Select>

                        <div className="dependency-actions-container">
                            <TextField
                                type="number"
                                value={selectedDependencyOffsetDays}
                                onChange={(e) =>
                                    props.setSelectedDependencyOffsetDays(
                                        Math.max(0, Number(e.target.value))
                                    )
                                }
                                disabled={!selectedDependencyId}
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                }}
                            />

                            <FontAwesomeIcon
                                icon={faCheck}
                                className={`cursor-pointer ${
                                    selectedDependencyId &&
                                    selectedDependencyOffsetDays > -1
                                        ? 'text-success'
                                        : 'text-muted'
                                }`}
                                size="lg"
                                onClick={props.confirmDependency}
                            />
                        </div>
                    </div>
                )}
            </Box>
        </div>
    );
};

export default EventDependencyEditorCell;
